/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import logoImage from '../assets/FINAL LOGO AK.png';
import '../styles/CompleteSignup.css';

const CompleteSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error

  useEffect(() => {
    const completeSignup = async () => {
      // Check if we have the required data
      const { email, emailVerified, signupData } = location.state || {};

      if (!email || !emailVerified || !signupData) {
        navigate('/signup');
        return;
      }

      try {
        // Complete the signup with verified email
        await signup(signupData);
        
        setStatus('success');
        
        // The signup function in AuthContext will handle role-based routing
        // Just wait a moment to show success animation
        setTimeout(() => {
          // AuthContext should have already redirected, but just in case
          navigate('/');
        }, 2000);
      } catch (err) {
        console.error('Error completing signup:', err);
        setStatus('error');
        
        // Redirect to signup after showing error
        setTimeout(() => {
          navigate('/signup', {
            state: {
              error: err.message || 'Failed to complete signup. Please try again.'
            }
          });
        }, 3000);
      }
    };

    completeSignup();
  }, [location, navigate, signup]);

  return (
    <div className="complete-signup-container">
      <div className="complete-signup-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <motion.div 
        className="complete-signup-card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <img src={logoImage} alt="AksharJobs Logo" className="signup-logo" />

        {status === 'processing' && (
          <motion.div
            className="status-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="spinner-wrapper">
              <FontAwesomeIcon icon={faSpinner} spin className="spinner-icon" />
            </div>
            <h2>Creating Your Account</h2>
            <p>Please wait while we set up your account...</p>
            <div className="progress-dots">
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0 }}
              />
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
              />
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
              />
            </div>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            className="status-content success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <motion.div
              className="success-icon-wrapper"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
            </motion.div>
            <h2>Account Created Successfully!</h2>
            <p>Welcome to AksharJobs. Redirecting you to your dashboard...</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            className="status-content error"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="error-icon-wrapper">
              ⚠️
            </div>
            <h2>Something Went Wrong</h2>
            <p>We couldn't complete your signup. Redirecting back to signup page...</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default CompleteSignup;

